<template>
  <div class="flex flex-col gap-4 w-full h-full">
    <div class="flex flex-col gap-2">
      <h2 class="text-xl font-semibold text_color">Meeting disposition</h2>
      <p class="text-sm text_color opacity-60">Customize and manage meeting outcomes</p>
    </div>
    <div v-if="isLoading && dispositions.length === 0" class="max-h-80 overflow-hidden">
      <SkeletonLoader :page="loaderPages.NAV_LIST" />
    </div>
    <CustomContainer 
      v-else-if="dispositions?.length > 0" 
      :no-background-color="true" 
      :border-hidden="true"
      class="w-1/2"
    >
      <div
        class="flex flex-col gap-2 max-h-80 overflow-y-auto add_scroll_bar"
        @scroll="handleScroll"
        ref="scrollContainer"
      >
        <div class="flex flex-col gap-2">
          <MeetingDispositionItem
            v-for="(disposition, index) in dispositions"
            :key="disposition.id || index"
            :disposition="disposition.disposition || disposition"
            @remove="removeDisposition(disposition.id || index, disposition.disposition || disposition)"
          />
          <div v-if="isLoadingMore" class="flex flex-col gap-2 p-2">
            <SkeletonLoader :page="loaderPages.NAV_LIST" />
          </div>
        </div>
      </div>
    </CustomContainer>
    
    <div class="mt-3">
      <a-button
        @click="showAddModal = true"
        class="secondary_medium w-fit"
        :loading="isCreating"
      >
        <ThemeImage
          :icon="imageNames.ADD_GREY"
          alt="add_disposition"
          class="size-5"
        />
        <span class="text-[14px] font-semibold">Add disposition</span>
      </a-button>
    </div>

    <CustomModal
      title="Add Disposition"
      v-model:visible="showAddModal"
      :width="'400px'"
    >
      <template #default>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <span class="text-sm text_color font-medium">Disposition Name</span>
            <CustomInput
              v-model="newDispositionName"
              type="primary"
              placeholder="Enter disposition name"
              format="text"
              :auto-focus="true"
              @keyup.enter="handleAddDisposition"
            />
          </div>
        </div>
      </template>
      
      <template #footer>
        <div class="flex flex-row gap-2 justify-end">
          <a-button @click="handleCancelAdd" class="secondary_medium">
            Cancel
          </a-button>
          <a-button
            @click="handleAddDisposition"
            class="primary_medium"
            :disabled="!newDispositionName.trim()"
            :loading="isCreating"
          >
            Add Disposition
          </a-button>
        </div>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import { message } from "ant-design-vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { 
  fetchMeetingDispositions, 
  createMeetingDisposition, 
  deleteMeetingDisposition 
} from "@/src/utils/meetingsUtils.js";
import MeetingDispositionItem from "./MeetingDispositionItem.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";

const dispositions = ref([]);
const showAddModal = ref(false);
const newDispositionName = ref('');
const isLoading = ref(false);
const isCreating = ref(false);
const isLoadingMore = ref(false);
const currentPage = ref(1);
const pageSize = ref(10);
const hasMore = ref(true);
const totalCount = ref(0);
const scrollContainer = ref(null);


const loadDispositions = async (page = 1, reset = false) => {
  try {
    if (reset) {
      isLoading.value = true;
      currentPage.value = 1;
      dispositions.value = [];
      hasMore.value = true;
    } else {
      isLoadingMore.value = true;
    }

    const params = {
      page: page,
      page_size: pageSize.value
    };

    const response = await fetchMeetingDispositions(params);
    
    if (response?.data) {
      const newDispositions = response.data || [];
      
      if (reset) {
        dispositions.value = newDispositions;
      } else {
        dispositions.value = [...dispositions.value, ...newDispositions];
      }
      
      totalCount.value = response.total || 0;
      currentPage.value = page;
      
      hasMore.value = dispositions.value.length < totalCount.value;
      
    } 
  } catch (error) {
    console.error('Error fetching dispositions:', error);
    message.error('Error fetching meeting dispositions');
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
  }
};

const removeDisposition = async (dispositionId, dispositionName) => {
  try {
    if (typeof dispositionId === 'string') {
      const index = dispositions.value.findIndex(d => 
        (d.disposition || d) === dispositionName
      );
      if (index !== -1) {
        dispositions.value.splice(index, 1);
        totalCount.value -= 1;
        message.success('Disposition removed');
      }
      return;
    }

    await deleteMeetingDisposition(dispositionId);
    dispositions.value = dispositions.value.filter(d => d.id !== dispositionId);
    totalCount.value -= 1;
    message.success('Disposition deleted successfully');
  } catch (error) {
    console.error('Error deleting disposition:', error);
    message.error('Failed to delete disposition');
  }
};

const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  if (
    scrollHeight - scrollTop <= clientHeight + 1 &&
    !isLoadingMore.value &&
    hasMore.value
  ) {
    loadNextPage();
  }
};

const loadNextPage = async () => {
  if (!hasMore.value || isLoadingMore.value) return;
  
  const nextPage = currentPage.value + 1;
  await loadDispositions(nextPage, false);
};


const handleAddDisposition = async () => {
  if (!newDispositionName.value.trim()) {
    message.warning('Please enter a disposition name');
    return;
  }

  const exists = dispositions.value.some(d => 
    (d.disposition || d).toLowerCase() === newDispositionName.value.trim().toLowerCase()
  );
  
  if (exists) {
    message.warning('This disposition already exists');
    return;
  }

  try {
    isCreating.value = true;
    const disposition = await createMeetingDisposition(newDispositionName.value.trim());
    
    if (!disposition) {
      message.error('Failed to create disposition');
      return;
    }
    
    dispositions.value.push(disposition);
    totalCount.value += 1;
    newDispositionName.value = '';
    showAddModal.value = false;
    message.success('Disposition added successfully');
    await nextTick();
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  } catch (error) {
    console.error('Error creating disposition:', error);
    message.error('Failed to create disposition');
  } finally {
    isCreating.value = false;
  }
};

const handleCancelAdd = () => {
  newDispositionName.value = '';
  showAddModal.value = false;
};

onMounted(() => {
  loadDispositions(1, true);
});
</script>

